<script setup lang="ts">
import { useDark } from '@vueuse/core'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { BUBBLE_CSS_PRESETS, BUBBLE_THEMES } from '../../../../../constants/bubble-themes'
import { useSettings } from '../../../../../stores/settings'
import { Button } from '../../../../misc'

interface Emits {
  (e: 'next'): void
  (e: 'skip'): void
}

const emit = defineEmits<Emits>()

const { t } = useI18n()
const settingsStore = useSettings()
const isDark = useDark()
const contentReady = inject<import('vue').Ref<boolean>>('onboarding-content-ready', ref(true))

const selectedTheme = ref(settingsStore.chatBubbleTheme || 'default')

const themeOptions = [
  { id: 'default', label: '默认', desc: '经典绿白配色' },
  { id: 'latte', label: '拿铁', desc: '毛玻璃咖啡风' },
  { id: 'qq', label: 'QQ', desc: '蓝白经典风格' },
  { id: 'wechat', label: '微信', desc: '绿白聊天风格' },
  { id: 'glass', label: '玻璃', desc: '透明毛玻璃效果' },
  { id: 'blue-pink', label: '蓝粉', desc: '柔和蓝粉配色' },
] as const

// 为每个主题生成预览用的 scoped style
const previewStyleElements = ref<HTMLStyleElement[]>([])

function getPreviewClass(themeId: string, side: 'user' | 'ai') {
  return `onboarding-preview-${themeId}-${side}`
}

function buildPreviewStyles() {
  // 清理旧的
  previewStyleElements.value.forEach(el => el.remove())
  previewStyleElements.value = []

  for (const theme of themeOptions) {
    const styleEl = document.createElement('style')
    const userClass = getPreviewClass(theme.id, 'user')
    const aiClass = getPreviewClass(theme.id, 'ai')

    if (theme.id in BUBBLE_CSS_PRESETS) {
      // CSS 预设：将 .chat-bubble-user/.chat-bubble-ai 替换为预览专用类名
      let css = BUBBLE_CSS_PRESETS[theme.id]
      css = css.replace(/\.chat-bubble-user/g, `.${userClass}`)
      css = css.replace(/\.chat-bubble-ai/g, `.${aiClass}`)
      styleEl.textContent = css
    }
    else if (theme.id in BUBBLE_THEMES) {
      // 颜色预设：根据当前暗色模式选择对应色板
      const palette = BUBBLE_THEMES[theme.id as keyof typeof BUBBLE_THEMES]
      const colors = isDark.value ? palette.dark : palette.light
      styleEl.textContent = `
        .${userClass} { background-color: ${colors.userBg} !important; color: ${colors.userText} !important; }
        .${aiClass} { background-color: ${colors.aiBg} !important; color: ${colors.aiText} !important; }
      `
    }

    document.head.appendChild(styleEl)
    previewStyleElements.value.push(styleEl)
  }
}

const isSelected = computed(() => (id: string) => selectedTheme.value === id)

watch(selectedTheme, (val) => {
  settingsStore.chatBubbleTheme = val
})

watch(isDark, () => {
  buildPreviewStyles()
})

onMounted(() => {
  buildPreviewStyles()
})

onUnmounted(() => {
  previewStyleElements.value.forEach(el => el.remove())
})
</script>

<template>
  <div class="min-h-0 flex flex-1 flex-col gap-4">
    <template v-if="contentReady">
      <div
        v-motion
        :initial="{ opacity: 0, y: 16 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="380"
        class="flex-shrink-0"
      >
        <h2 class="text-xl text-neutral-800 md:text-2xl dark:text-neutral-100">
          {{ t('settings.dialogs.onboarding.appearance.title') }}
        </h2>
        <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {{ t('settings.dialogs.onboarding.appearance.description') }}
        </p>
      </div>

      <div class="flex-1 overflow-y-auto space-y-4">
        <!-- 亮色/暗色模式 -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 18 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="380"
          :delay="120"
        >
          <label class="mb-2 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
            {{ t('settings.dialogs.onboarding.appearance.colorMode') }}
          </label>
          <div class="grid grid-cols-2 gap-3">
            <div
              class="cursor-pointer border-2 rounded-xl p-3 transition-all"
              :class="!isDark
                ? 'border-primary-500 bg-primary-50/50'
                : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'"
              @click="isDark = false"
            >
              <div class="flex items-center gap-2">
                <div class="i-solar:sun-bold-duotone h-5 w-5 text-amber-500" />
                <span class="text-sm font-medium" :class="!isDark ? 'text-primary-700' : 'text-neutral-600 dark:text-neutral-400'">
                  {{ t('settings.dialogs.onboarding.appearance.lightMode') }}
                </span>
              </div>
              <div class="mt-2 h-8 rounded-lg bg-white shadow-sm ring-1 ring-neutral-200" />
            </div>
            <div
              class="cursor-pointer border-2 rounded-xl p-3 transition-all"
              :class="isDark
                ? 'border-primary-500 bg-primary-900/20'
                : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'"
              @click="isDark = true"
            >
              <div class="flex items-center gap-2">
                <div class="i-solar:moon-bold-duotone h-5 w-5 text-indigo-400" />
                <span class="text-sm font-medium" :class="isDark ? 'text-primary-300' : 'text-neutral-600 dark:text-neutral-400'">
                  {{ t('settings.dialogs.onboarding.appearance.darkMode') }}
                </span>
              </div>
              <div class="mt-2 h-8 rounded-lg bg-neutral-800 shadow-sm ring-1 ring-neutral-700" />
            </div>
          </div>
        </div>

        <!-- 气泡样式 -->
        <div
          v-motion
          :initial="{ opacity: 0, y: 20 }"
          :enter="{ opacity: 1, y: 0 }"
          :duration="400"
          :delay="220"
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <div
            v-for="theme in themeOptions"
            :key="theme.id"
            class="cursor-pointer border-2 rounded-xl p-3 transition-all"
            :class="isSelected(theme.id)
              ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
              : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'"
            @click="selectedTheme = theme.id"
          >
            <!-- 主题名称 -->
            <div class="mb-2 flex items-center justify-between">
              <span class="text-sm font-medium" :class="isSelected(theme.id) ? 'text-primary-700 dark:text-primary-300' : 'text-neutral-700 dark:text-neutral-300'">
                {{ theme.label }}
              </span>
              <span class="text-xs text-neutral-400">{{ theme.desc }}</span>
            </div>
            <!-- 气泡预览（使用真实 CSS 类） -->
            <div class="rounded-lg bg-neutral-100/80 p-2.5 space-y-1.5 dark:bg-neutral-800/80">
              <div class="flex justify-start">
                <div
                  :class="getPreviewClass(theme.id, 'ai')"
                  class="max-w-[75%] rounded-lg px-3 py-1.5 text-xs"
                >
                  你好呀～
                </div>
              </div>
              <div class="flex justify-end">
                <div
                  :class="getPreviewClass(theme.id, 'user')"
                  class="max-w-[75%] rounded-lg px-3 py-1.5 text-xs"
                >
                  嗨，今天过得怎么样？
                </div>
              </div>
              <div class="flex justify-start">
                <div
                  :class="getPreviewClass(theme.id, 'ai')"
                  class="max-w-[75%] rounded-lg px-3 py-1.5 text-xs"
                >
                  很开心见到你！
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-motion
        :initial="{ opacity: 0 }"
        :enter="{ opacity: 1 }"
        :duration="300"
        :delay="340"
        class="flex gap-2"
      >
        <button
          class="flex-shrink-0 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
          @click="emit('skip')"
        >
          {{ t('settings.dialogs.onboarding.skipStep') }}
        </button>
        <Button
          class="flex-1"
          :label="t('settings.dialogs.onboarding.next')"
          @click="emit('next')"
        />
      </div>
    </template>
  </div>
</template>
